import { useMemo } from 'react';
import { CandleType } from 'grts-utils/types/ProfitCalcs';
import { TimeFrame } from 'grts-utils/types/TimeFrame';
import LegacyRsiChart from '../../../../../../components/Charts/Rsi';
import { XAxisInfo } from '../../../../../../components/MultiChart';
import {
  RSIItem,
  SingleProfitCalculationApiResponse,
} from '../../../../../ProfitCalculations/types';
import { useGetRsiSignalsQuery } from '../../../../../../store/services/api';
import { useResultData } from '../../context';
import {
  flattenShorterT,
  windowForLiveState,
  windowForTransaction,
} from '../../derived/candles';

export type RsiSide =
  | 'opposite'
  | 'same'
  | 'mrcHigher'
  | 'mrcShorter'
  | 'mrcMulti';

interface Props {
  label: string;
  side: RsiSide;
  txId?: number;
  live?: boolean;
  /** Injected by MultiChart. */
  width?: number;
  height?: number;
  showXAxis?: boolean;
  xAxisInfo?: XAxisInfo;
}

interface ResolvedConfig {
  period: string;
  threshold: string;
  candleType: CandleType;
  timeFrame: TimeFrame;
  childTimeFrame: TimeFrame;
}

interface MrcSlice {
  threshold: number;
  values: RSIItem[];
}

const mrcSliceFor = (
  spc: SingleProfitCalculationApiResponse,
  side: 'mrcHigher' | 'mrcShorter' | 'mrcMulti',
): MrcSlice | null => {
  const config = spc.mrcRsiConfig as
    | {
        higherTRsiThreshold?: number;
        shorterTRsiThreshold?: number;
        multiTRsiThreshold?: number;
        rsiThreshold?: number;
      }
    | null
    | undefined;
  if (!config) return null;
  if (side === 'mrcHigher') {
    return {
      threshold: config.higherTRsiThreshold ?? config.rsiThreshold ?? 70,
      values: spc.mrcRsiHigherTValues ?? [],
    };
  }
  if (side === 'mrcShorter') {
    return {
      threshold: config.shorterTRsiThreshold ?? config.rsiThreshold ?? 70,
      values: spc.mrcRsiShorterTValues ?? [],
    };
  }
  return {
    threshold: config.multiTRsiThreshold ?? config.rsiThreshold ?? 70,
    values: spc.mrcRsiMultiTValues ?? [],
  };
};

const RsiPanel = ({
  label,
  side,
  txId,
  live,
  width,
  height,
  showXAxis,
  xAxisInfo,
}: Props) => {
  const { raw } = useResultData();
  const candleWindow = useMemo(() => {
    if (live) return windowForLiveState(raw);
    if (txId !== undefined) return windowForTransaction(txId, raw);
    return { start: 0, end: 0, candles: [], transactions: [] };
  }, [raw, txId, live]);

  const isMrc =
    side === 'mrcHigher' || side === 'mrcShorter' || side === 'mrcMulti';

  // RSI is fetched ONCE for the whole duration: every panel (live + each
  // expanded transaction) queries the same full range, so RTK Query dedupes to a
  // single request and opening a transaction never triggers a new fetch. Each
  // panel then slices the values to its own window below.
  const fullRange = useMemo(() => {
    let start = Infinity;
    let end = 0;
    for (const candle of flattenShorterT(raw)) {
      if (candle.startTimestamp < start) start = candle.startTimestamp;
      if (candle.endTimestamp > end) end = candle.endTimestamp;
    }
    return { start: start === Infinity ? 0 : start, end };
  }, [raw]);

  const resolved: ResolvedConfig | null = useMemo(() => {
    const indicatorConfig = raw.indicatorConfig;
    if (side === 'opposite' && indicatorConfig) {
      return {
        period: String(indicatorConfig.oppositeOrderPeriod),
        threshold: String(indicatorConfig.oppositeOrderThreshold),
        candleType: indicatorConfig.oppositeOrderCandleType,
        timeFrame: indicatorConfig.oppositeOrderTimeFrame,
        childTimeFrame: indicatorConfig.oppositeOrderChildTimeFrame,
      };
    }
    if (side === 'same' && indicatorConfig) {
      return {
        period: String(indicatorConfig.sameOrderPeriod),
        threshold: String(indicatorConfig.sameOrderThreshold),
        candleType: indicatorConfig.sameOrderCandleType,
        timeFrame: indicatorConfig.sameOrderTimeFrame,
        childTimeFrame: indicatorConfig.sameOrderChildTimeFrame,
      };
    }
    return null;
  }, [raw.indicatorConfig, side]);

  const { data } = useGetRsiSignalsQuery(
    {
      basicParams: {
        baseAsset: raw.baseAsset,
        quoteAsset: raw.quoteAsset,
        exchange: raw.exchange,
        startTimestamp: fullRange.start,
        endTimestamp: fullRange.end,
      },
      rsiConfig: resolved ?? undefined,
    },
    {
      skip: isMrc || !resolved || fullRange.start === 0 || fullRange.end === 0,
    },
  );

  const mrcSlice = isMrc
    ? mrcSliceFor(raw, side as 'mrcHigher' | 'mrcShorter' | 'mrcMulti')
    : null;
  const rawValues: RSIItem[] = isMrc
    ? mrcSlice?.values ?? []
    : data?.indicatorValues ?? [];
  const values = rawValues.filter(
    (value) =>
      value.timestamp >= candleWindow.start &&
      value.timestamp <= candleWindow.end,
  );
  const threshold = isMrc
    ? mrcSlice?.threshold ?? 70
    : resolved
    ? parseInt(resolved.threshold, 10)
    : 70;

  return (
    <LegacyRsiChart
      label={label}
      rsi={values}
      overExtensionThreshold={threshold}
      width={width}
      height={height}
      showXAxis={showXAxis}
      xAxisInfo={xAxisInfo}
    />
  );
};

export default RsiPanel;
